import { WallShapeType } from "../../types/floorplannerTypes";
import * as THREE from "three";
import { createFirstWallSegment } from "./createFirstWallSegment";
import { createFullWallShape, createLineShape } from "./createFullWallShape";
import { createLastWallSegment } from "./createLastWallSegment";
import { createWallSegment } from "./createWallSegment";

/**
 * Create THREE.Shape objects for wall geometry based on shape type.
 *
 * This function generates THREE.Shape objects for different parts of a wall:
 * - "inner": Filled wall shape with cutouts
 * - "left": Left outline shape (perpendicular offset)
 * - "right": Right outline shape (perpendicular offset)
 * - "endCaps": Endcap shapes at wall endpoints
 *
 * These shapes are created and stored in renderStore Maps for geometry calculations.
 * However, the left/right outline shapes are not rendered directly in fillMeshWalls.
 * Instead, WallOutlineStructure component renders the outlines as segmented LineSegments2
 * with gaps at connection points.
 *
 * Mid-point connections continue to use clipping polygons (in fillMeshWalls) for
 * trimming at connection points.
 *
 * @param wallShape - Wall shape data containing paths and metadata
 * @param shapeType - Type of shape to create ("inner", "left", "right", "endCaps")
 * @param lineWeight - Line weight for outlines
 * @param wallWidth - Width of the wall
 * @returns Array of THREE.Shape objects
 */
export const createShapes = (
  wallShape: WallShapeType,
  shapeType: string,
  lineWeight: number,
  wallWidth: number,
): THREE.Shape[] => {
  const shapes: THREE.Shape[] = [];
  if (!wallShape) {
    return shapes;
  }
  const wall = wallShape.wall;
  const wallLength = wall.start.distanceTo(wall.end);
  const wallDirection = new THREE.Vector2()
    .subVectors(wall.end, wall.start)
    .normalize();
  const perpendicular = new THREE.Vector2(
    -wallDirection.y,
    wallDirection.x,
  );
  const halfWallWidth = wallWidth / 2;

  if (shapeType === "endCaps") {
    if (wallShape.paths.length > 4) {
      // Split end caps into separate shapes
      //const startEndCap = createFullWallShape(wallShape.paths.slice(0, 4));
      const startEndCap = createLineShape(wallShape.paths[0], wallShape.paths[1]);
      shapes.push(startEndCap);
      //const endEndCap = createFullWallShape(wallShape.paths.slice(4));
      const endEndCap = createLineShape(wallShape.paths.slice(4)[0], wallShape.paths.slice(4)[1]);
      shapes.push(endEndCap);
      return shapes;
    } else if (wallShape.paths.length === 4) {
      //const shape = createFullWallShape(wallShape.paths);
      const shape = createLineShape(wallShape.paths[0], wallShape.paths[1]);
      shapes.push(shape);
      return shapes;
    }
  } else if (shapeType === "left") {
    // Left outline shape - represents the left edge of the wall
    // Note: These outline shapes are created and stored in renderStore for geometry calculations,
    // but are not rendered directly. Instead, WallOutlineStructure renders segmented lines
    // with gaps at connection points using OutlineSegment data.
    const start = wallShape.paths[0];
    const end = wallShape.paths[1];
    const shape = createLineShape(start, end);
    shapes.push(shape);
    return shapes
  } else if (shapeType === "right") {
    // Right outline shape - represents the right edge of the wall
    // Note: These outline shapes are created and stored in renderStore for geometry calculations,
    // but are not rendered directly. Instead, WallOutlineStructure renders segmented lines
    // with gaps at connection points using OutlineSegment data.
    const start = wallShape.paths[0];
    const end = wallShape.paths[1];
    const shape = createLineShape(start, end);
    shapes.push(shape);
    return shapes;
  } else {
    if (!wall.cutouts || wall.cutouts.length === 0) {
      const shape = createFullWallShape(wallShape.paths);
      shapes.push(shape);
    } else {
      wall.cutouts.sort((a, b) => a.positionFromStart - b.positionFromStart);
      for (let i = 0; i <= wall.cutouts.length; i++) {
        const cutout = i < wall.cutouts.length ? wall.cutouts[i] : null;
        const startPosition =
          i === 0
            ? 0
            : wall.cutouts[i - 1].positionFromStart +
              wall.cutouts[i - 1].length;
        const endPosition =
          i < wall.cutouts.length
            ? wall.cutouts[i].positionFromStart
            : wallLength;
        if (endPosition > startPosition) {
          let shape: THREE.Shape;
          if (i < wall.cutouts.length && !cutout?.appliesTo.has(shapeType)) {
            const cutoutStartPosition = cutout?.positionFromStart || 0;
            const cutoutEndPosition =
              cutoutStartPosition + (cutout?.length || 0);
            shape = createWallSegment(
              wall.start,
              cutoutStartPosition,
              cutoutEndPosition,
              wallDirection,
              perpendicular,
              lineWeight,
              wallWidth,
              shapeType,
            );
            shapes.push(shape);
          }
          if (i === 0) {
            // First segment: use input vertices for top
            shape = createFirstWallSegment(
              wall.start,
              startPosition,
              endPosition,
              wallDirection,
              perpendicular,
              lineWeight,
              wallWidth,
              wallShape.paths[0],
              wallShape.paths[1],
              shapeType,
            );
          } else if (i === wall.cutouts.length) {
            // Last segment: use input vertices for bottom
            shape = createLastWallSegment(
              wall.start,
              startPosition,
              endPosition,
              wallDirection,
              perpendicular,
              lineWeight,
              wallWidth,
              wallShape.paths[2],
              wallShape.paths[3],
              shapeType,
            );
          } else {
            // Middle segments
            shape = createWallSegment(
              wall.start,
              startPosition,
              endPosition,
              wallDirection,
              perpendicular,
              lineWeight,
              wallWidth,
              shapeType,
            );
          }
          shapes.push(shape);
        }
      }
    }
  }

  return shapes;
};
